import dayjs from 'dayjs'

class Order<T> {

  private readonly array: T[] = [];

  constructor(original: T[]) {
    this.array = [...original]
  }

  private isString(value: any): value is string {
    return typeof value === 'string';
  }

  private isBoolean(value: any): value is boolean {
    return typeof value === 'boolean';
  }

  private orderByAlphabetical(direction: 'asc' | 'desc', key: keyof T): Order<T> {
    this.array.sort((a, b) => {

      const aCompare = a[key];
      const bCompare = b[key];

      if (!this.isString(aCompare) || !this.isString(bCompare)) {
        console.warn(`Unable to alphaetically compare ${aCompare} and ${bCompare} for array: `, this.array)
        return 0;
      }

      const result = aCompare.toLowerCase() < bCompare.toLowerCase() ? -1 : 1

      switch (direction) {
        case 'asc': return result;
        case 'desc': return result * -1;
      }

    })
    return this;
  }

  private orderByBoolean(prefer: boolean, key: keyof T): Order<T> {
    this.array.sort((a, b) => {

      const aCompare = a[key];
      const bCompare = b[key];

      if (!this.isBoolean(aCompare) || !this.isBoolean(bCompare)) {
        console.warn(`Unable to boolean compare ${aCompare} and ${bCompare} for array: `, this.array)
        return 0;
      }

      if (a[key] === b[key]) return 0;

      const result = a[key] ? -1 : 1

      switch (prefer) {
        case true: return result;
        case false: return result * -1;
      }

    })
    return this;
  }

  private orderByDate(direction: 'asc' | 'desc', key: keyof T): Order<T> {
    this.array.sort((a, b) => {

      const aCompare = a[key]
      const bCompare = b[key]

      const aDate = dayjs(aCompare)

      if(!aDate.isValid()) {
        console.warn(`Unable to date compare ${aCompare} and ${bCompare}, ${aCompare} is not a valid date`)
        return 0;
      }

      const bDate = dayjs(bCompare)

      if(!bDate.isValid()) {
        console.warn(`Unable to date compare ${aCompare} and ${bCompare}, ${bCompare} is not a valid date.`)
        return 0
      }

      if(direction == 'asc')  {
        return bDate.isBefore(aDate) ? 1 : -1
      }

      return aDate.isBefore(bDate) ? 1 : -1

    })

    return this;
  }

  private orderByNumeric(direction: 'asc' | 'desc', key: keyof T): Order<T> {
    this.array.sort((a, b) => {
      const aCompare = a[key];
      const bCompare = b[key];

      const aNum = typeof aCompare === 'number' ? aCompare : Number(aCompare);
      const bNum = typeof bCompare === 'number' ? bCompare : Number(bCompare);

      if (isNaN(aNum) || isNaN(bNum)) {
        console.warn(`Unable to numerically compare ${aCompare} and ${bCompare} for array: `, this.array);
        return 0;
      }

      if (direction === 'asc') {
        return aNum - bNum;
      } else {
        return bNum - aNum;
      }
    });
    return this;
  }

  numericAscendingBy(key: keyof T): Order<T> {
    return this.orderByNumeric('asc', key);
  }

  numericDescendingBy(key: keyof T): Order<T> {
    return this.orderByNumeric('desc', key);
  }

  dateAscendingBy(key: keyof T): Order<T> {
    return this.orderByDate('asc', key)
  }

  dateDescendingBy(key: keyof T): Order<T> {
    return this.orderByDate('desc', key)
  }

  alphabeticalBy(key: keyof T): Order<T> {
    return this.orderByAlphabetical('asc', key)
  }

  reverseAlphabeticalBy(key: keyof T): Order<T> {
    return this.orderByAlphabetical('desc', key)
  }

  trueBy(key: keyof T): Order<T> {
    return this.orderByBoolean(true, key)
  }

  falseBy(key: keyof T): Order<T> {
    return this.orderByBoolean(false, key)
  }

  toArray(): T[] {
    return this.array;
  }

}

export default function order<T>(arr: T[]): Order<T> {
  return new Order(arr);
}
